import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { BrowserModule }  from '@angular/platform-browser';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import { UIRouterModule ,UrlService} from '@uirouter/angular';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceModule } from './services/service.module';
import { EdlComplyStates } from './edl-comply.states';
import { EdlComplyComponent } from './edl-comply.component';
import { LayoutModule } from './layout/layout.module';
import { LoginModule } from './login/login.module';
import {LaddaModule} from 'angular2-ladda';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import {RegistrationModule} from './registration/registration.module';
import {PrintModule} from './print/print.module';
import isEqual from 'lodash-es/isEqual';
import { ComponentModule } from './components/components.module';
import { ToastrModule } from 'ngx-toastr';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { LocalStorageModule } from 'angular-2-local-storage';
import { environment } from 'src/environments/environment';
import { JwtModule } from '@auth0/angular-jwt';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { 
    APPLICATION_TYPE, 
    ApplicationTypes,
    BannerModule, 
    BASE_URL, 
    BUILD_CREATION_TIMESTAMP, 
    BUILD_VERSION_NUMBER, 
    COMMON_URL, 
    DateInterceptor, 
    FingerprintingInterceptor, 
    EdlCommonComponentsModule, 
    EdlCommonServicesModule, 
    EdlPageComponentsModule, 
    ErrorMessageHelpersService, 
    GlobalComplyErrorHandler, 
    HttpErrorHandler, 
    NAVIGATION_MAP_PROVIDER, 
    PagedResponseInterceptor, 
    SessionService, 
    QuillUrlService } from '@edl-development/edl-common';
import { ComplyNavigationMapProvider } from './services/navigation/comply-navigation-map.provider';
import { QuillModule } from 'ngx-quill';
import { ErrorTailorModule } from '@ngneat/error-tailor';
import { VERSION } from './version';
import { CplErrorMessageHelpersService } from './services/cpl-error-message-helper/cpl-error-message-helper.service';
import { redirectLegacyAuthenticatedPaths } from './edl-comply.states-config';
import { NavigationHistoryService } from './services/navigation-history.service';
import { EdlIconModule } from './components/edl-icon/edl-icon.module';

export function JwtModuleConfigFactory() {
    const token = localStorage.getItem('edl-comply.token');
    const parsed = token ? JSON.parse(token) : null;

    return parsed;
}

export function errorTailorConfigFactory(
    service: ErrorMessageHelpersService,
    cplService: CplErrorMessageHelpersService
) {
    return {
        ...service.generateErrorMap(),
        ...cplService.generateErrorMap()
    };
}

@NgModule({
    imports: [
        BrowserModule,
        HttpClientModule,
        BsDatepickerModule.forRoot(),
        LocalStorageModule.forRoot({
            prefix: 'edl-comply',
            storageType: 'localStorage'
        }),
        JwtModule.forRoot({
            config: {
                tokenGetter: JwtModuleConfigFactory,
                allowedDomains: [environment.BASEDOMAIN]
            },
        }),
        NgbModule,
        UIRouterModule.forRoot({
            states: EdlComplyStates,
            otherwise: {
                state: 'public.page-not-found',
                params: {}
            },
            useHash: false,
            config: redirectLegacyAuthenticatedPaths
        }),
        ServiceModule,
        LayoutModule,
        LoginModule,
        RegistrationModule,
        PrintModule,
        BrowserAnimationsModule,
        ComponentModule,
        EdlIconModule,
        EdlCommonComponentsModule,
        QuillModule.forRoot({
            modules: {
                history: {
                    delay: 2000,
                    maxStack: 500,
                    userOnly: true
                },
                toolbar: [
                        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                        ['url'],
                        ['bold', 'italic', 'underline','strike'],
                        [{ 'color': [] }, { 'background': [] }],
                        [{ 'align': [] }],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        ['clean']
                ]
            },
            theme: 'snow'
        }),
        EdlCommonServicesModule,
        EdlPageComponentsModule,
        LaddaModule.forRoot({
            style: 'zoom-in'
        }),
        ToastrModule.forRoot({
            timeOut: 7000,              // Longer timeout for accessibility (reading time)
            extendedTimeOut: 2000,      // Extended time on hover/focus
            progressBar: true,          // Visual time indicator
            closeButton: true,          // Keyboard accessible close button
            preventDuplicates: true,    // Prevent same message appearing twice
            maxOpened: 5,               // Limit simultaneous toasts to avoid overwhelming users
            autoDismiss: true,          // Auto-remove oldest when maxOpened reached
            positionClass: 'toast-top-right',

            // ARIA/Accessibility Configuration (WCAG 4.1.3):
            // ngx-toastr v16.2.0+ automatically provides:
            // - role="alert" on each toast container
            // - aria-live="assertive" for error/warning toasts (interrupts screen readers)
            // - aria-live="polite" for success/info toasts (waits for current speech)
            // - aria-atomic="true" for complete announcement of title + message
        }),
        BannerModule,
        LoadingBarModule,
        ErrorTailorModule.forRoot({
            errors: {
                                useFactory: errorTailorConfigFactory,
                                deps: [ErrorMessageHelpersService, CplErrorMessageHelpersService]
            },
            blurPredicate: (element) => {
                return element.tagName === 'INPUT' || element.tagName === 'SELECT' || element.tagName === 'TEXTAREA';
            }
                })
    ],
    declarations: [
        EdlComplyComponent
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: (navHistory: NavigationHistoryService) => () => {}, // Initialize NavigationHistoryService before routing
            deps: [NavigationHistoryService],
            multi: true
        },
        { provide: HTTP_INTERCEPTORS, useClass: DateInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpErrorHandler, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: FingerprintingInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: PagedResponseInterceptor, multi: true },
        { provide: APPLICATION_TYPE, useValue: ApplicationTypes.EDL_COMPLY },
        { provide: COMMON_URL, useValue: `${environment.BASEURI}/common` },
        { provide: BASE_URL, useValue: `${environment.BASEURI}/comply` },
        { provide: BUILD_VERSION_NUMBER, useValue: VERSION.version },
        { provide: BUILD_CREATION_TIMESTAMP, useValue: VERSION.timestamp },
        { provide: NAVIGATION_MAP_PROVIDER, useClass: ComplyNavigationMapProvider },
        {
            provide: ErrorHandler,
            useClass: GlobalComplyErrorHandler
        }
    ],
    bootstrap: [
        EdlComplyComponent
    ]
})

export class EdlComplyModule {
    constructor(
        private sessionService: SessionService,
        private urlService: UrlService,
        private quillUrlService: QuillUrlService
    ) {
        if (this.sessionService.isRestorable()) {
            this.sessionService.restore();
        }

        urlService.config.type('myjson', {
            encode: (obj) => JSON.stringify(obj),
            decode: (str) => JSON.parse(str),
            is: (val) => typeof(val) === 'object',
            pattern: /[^/]+/,
            equals: (a, b) => isEqual(a, b),
          });

        this.urlService.config.type('searchBy', {
            encode: (obj) => {
                return obj;
            },
            decode: (obj) => {
                return decodeURIComponent(obj);
            },
            is: (val) => {
                return typeof val === 'string';
            },
            pattern: /[^/]*/,
            equals: (a, b) =>  isEqual(a, b),

        });

        // Ensure the custom URL prompt module from edl-common is registered with Quill
        this.quillUrlService.registerUrlModule();
    }
}

